import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Package } from '@lifeomic/chromicons';
import {
  getKitSheetPatient,
  MONOSPACE_FONT,
  printMyOmeKitSheet,
  RqIdBarcodeArea,
  SPECIMEN,
} from './MyOmeKitSheet';
import { useMemberDetailStyles } from './styles';
import { MyOmeRequisition, useMyOmeRequisition } from './useMyOmeRequisition';

const messages = defineMessages({
  title: {
    id: 'MemberDetail.myOmeRequisitionTile.title',
    defaultMessage: 'MyOme requisition',
  },
  loadError: {
    id: 'MemberDetail.myOmeRequisitionTile.loadError',
    defaultMessage: "Couldn't load MyOme requisition",
  },
  retry: {
    id: 'MemberDetail.myOmeRequisitionTile.retry',
    defaultMessage: 'Retry',
  },
  print: {
    id: 'MemberDetail.myOmeRequisitionTile.print',
    defaultMessage: 'Print',
  },
  noRqIdYet: {
    id: 'MemberDetail.myOmeRequisitionTile.noRqIdYet',
    defaultMessage: 'No RQ id yet',
  },
  noRqId: {
    id: 'MemberDetail.myOmeRequisitionTile.noRqId',
    defaultMessage: 'No RQ id',
  },
});

const useStyles = makeStyles(theme => ({
  staticTile: {
    '&&': {
      cursor: 'default',
      '&:hover': { boxShadow: 'none' },
    },
  },
  rqId: {
    fontFamily: MONOSPACE_FONT,
    fontWeight: 600,
    overflowWrap: 'anywhere',
  },
  statusPill: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    padding: '1px 8px',
  },
  statusPillOnHold: {
    backgroundColor: theme.palette.orange[100],
    color: theme.palette.orange[800],
  },
  statusPillFailed: {
    backgroundColor: theme.palette.red[100],
    color: theme.palette.red[800],
  },
  trailing: {
    flexShrink: 0,
  },
}));

export type MyOmeRequisitionTileBehavior = {
  /** Shown in place of the RQ id while there isn't one. */
  statusPill?: { tone: 'on-hold' | 'failed'; label: MessageDescriptor };
  /** Shown under the status pill. */
  statusLine?: string;
  /** The kit sheet's block above the patient table. The tile prints on click when set. */
  requisitionArea?: React.ReactNode;
};

/**
 * What the tile shows and prints for a requisition. Only a Task with an RQ id
 * prints for now; the reason-code warnings (FLENG-143) fill in the rest.
 */
export const getMyOmeRequisitionTileBehavior = ({
  rqId,
  status,
}: MyOmeRequisition): MyOmeRequisitionTileBehavior => {
  if (rqId) {
    return { requisitionArea: <RqIdBarcodeArea rqId={rqId} /> };
  }
  return {
    statusPill:
      status === 'failed'
        ? { tone: 'failed', label: messages.noRqId }
        : { tone: 'on-hold', label: messages.noRqIdYet },
  };
};

type TileFrameProps = {
  onClick?: () => void;
  trailing?: React.ReactNode;
  children?: React.ReactNode;
};

const TileFrame = ({ onClick, trailing, children }: TileFrameProps) => {
  const { formatMessage } = useIntl();
  const classes = useMemberDetailStyles();
  const ownClasses = useStyles();
  const content = (
    <>
      <div className={classes.landingCompactTileIconBadge}>
        <Package className={classes.landingCompactTileIcon} />
      </div>
      <div className={classes.landingCompactTileContent}>
        <Text size="subbody" weight="bold" className={classes.landingTileTitle}>
          {formatMessage(messages.title)}
        </Text>
        {children}
      </div>
      {trailing ? <div className={ownClasses.trailing}>{trailing}</div> : null}
    </>
  );
  const className = clsx(classes.landingTileBase, classes.landingCompactTile);
  return onClick ? (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div
      role="group"
      aria-label={formatMessage(messages.title)}
      className={clsx(className, ownClasses.staticTile)}
    >
      {content}
    </div>
  );
};

type FoundTileProps = {
  requisition: MyOmeRequisition;
  patient: fhir3.Patient;
};

const FoundTile = ({ requisition, patient }: FoundTileProps) => {
  const { formatMessage } = useIntl();
  const classes = useMemberDetailStyles();
  const ownClasses = useStyles();
  const cancelPrintRef = useRef<() => void>();
  const { statusPill, statusLine, requisitionArea } =
    getMyOmeRequisitionTileBehavior(requisition);

  useEffect(() => () => cancelPrintRef.current?.(), []);

  // Browsers without a reliable frame `afterprint` would leave a "printing"
  // lock stuck, so a new click replaces any print that hasn't opened yet.
  const print = requisitionArea
    ? () => {
        cancelPrintRef.current?.();
        cancelPrintRef.current = printMyOmeKitSheet({
          patient: getKitSheetPatient(patient),
          requisitionArea,
        });
      }
    : undefined;

  return (
    <TileFrame
      onClick={print}
      trailing={
        print ? (
          <Text size="caption" className={classes.landingTileDescription}>
            {formatMessage(messages.print)}
          </Text>
        ) : null
      }
    >
      {requisition.rqId ? (
        <Text
          size="caption"
          className={clsx(classes.landingTileDescription, ownClasses.rqId)}
        >
          {requisition.rqId}
        </Text>
      ) : null}
      {statusPill ? (
        <span
          className={clsx(
            ownClasses.statusPill,
            statusPill.tone === 'failed'
              ? ownClasses.statusPillFailed
              : ownClasses.statusPillOnHold,
          )}
        >
          {formatMessage(statusPill.label)}
        </span>
      ) : null}
      {statusLine ? (
        <Text size="caption" className={classes.landingTileDescription}>
          {statusLine}
        </Text>
      ) : null}
      <Text size="caption" className={classes.landingTileDescription}>
        {SPECIMEN}
      </Text>
    </TileFrame>
  );
};

export type MyOmeRequisitionTileProps = {
  /** The unmasked Patient: MyOme matches the box by these identifiers. */
  patient: fhir3.Patient;
};

/**
 * The "MyOme requisition" tile: the patient's original requisition, and the
 * one place clinicians print its kit sheet. Absent when there's no
 * requisition Task, so the page looks as it did before MyOme orders.
 */
export const MyOmeRequisitionTile = ({
  patient,
}: MyOmeRequisitionTileProps) => {
  const { formatMessage } = useIntl();
  const classes = useMemberDetailStyles();
  const result = useMyOmeRequisition(patient.id ?? '');

  switch (result.state) {
    case 'none':
      return null;
    case 'loading':
      return (
        <TileFrame>
          <DotLoader size={0} />
        </TileFrame>
      );
    case 'error':
      return (
        <TileFrame
          trailing={
            <Button variant="text" onClick={result.retry}>
              {formatMessage(messages.retry)}
            </Button>
          }
        >
          <Text size="caption" className={classes.landingTileDescription}>
            {formatMessage(messages.loadError)}
          </Text>
        </TileFrame>
      );
    case 'found':
      return <FoundTile requisition={result.requisition} patient={patient} />;
  }
};
